/**
 * **Navigate** — `docs/activity.md` §10.3, Board 14 B.
 *
 * The first time on a phone, a sheet: *Navigate to 412 Orchard Ridge Dr* /
 * *Mechanicsburg, PA 17050*, then **Apple Maps** (iOS only) · **Google Maps**
 * · **Waze**, and *Always use this* (on). The choice is remembered in the
 * page's state (`navApp`, per device — §4.6), and from then on Navigate
 * opens that app straight away with the place's coordinates. On a computer
 * or an iPad it opens Google Maps in a new tab.
 */
import { useState } from 'react'
import type { NavApp } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Glyph } from '../glyphs.js'
import { StopSheet } from './sheet.js'

export interface Destination {
  latitude: number | null
  longitude: number | null
  /** The address in words, for a place with no coordinates. */
  address: string | null
}

/** Apple Maps is offered on an iPhone or an iPad only. */
export function isApple(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iPhone|iPad|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

/** The three apps' own links (§10.3). */
export function navUrl(app: NavApp, to: Destination): string {
  const at = to.latitude !== null && to.longitude !== null ? `${to.latitude},${to.longitude}` : null
  const words = encodeURIComponent(to.address ?? '')
  if (app === 'apple') return at ? `maps://?daddr=${at}` : `maps://?daddr=${words}`
  if (app === 'waze') return at ? `https://waze.com/ul?ll=${at}&navigate=yes` : `https://waze.com/ul?q=${words}&navigate=yes`
  return `https://www.google.com/maps/dir/?api=1&destination=${at ?? words}`
}

export function openNav(app: NavApp, to: Destination, phone: boolean): void {
  const url = navUrl(phone ? app : 'google', to)
  if (phone) window.location.assign(url)
  else window.open(url, '_blank', 'noopener')
}

const APPS: { key: NavApp; name: string }[] = [
  { key: 'apple', name: 'Apple Maps' },
  { key: 'google', name: 'Google Maps' },
  { key: 'waze', name: 'Waze' },
]

export function NavigateSheet({
  street, place, chosen, onChoose, onClose,
}: {
  /** *412 Orchard Ridge Dr*. */
  street: string
  /** *Mechanicsburg, PA 17050*. */
  place: string
  chosen: NavApp | null
  /** The app, and whether to remember it. */
  onChoose: (app: NavApp, always: boolean) => void
  onClose: () => void
}) {
  const [always, setAlways] = useState(true)
  const apps = APPS.filter((app) => app.key !== 'apple' || isApple())
  /* Board 14 B: the first app offered starts ticked, *remembered for next time* under it. */
  const [picked, setPicked] = useState<NavApp | null>(chosen ?? apps[0]?.key ?? null)

  return (
    <StopSheet title={`Navigate to ${street}`} sub={place} onClose={onClose} minHeight={390}>
      {apps.map((app) => (
        <button key={app.key} type="button"
          onClick={() => { setPicked(app.key); onChoose(app.key, always) }}
          className="flex h-[58px] w-full items-center gap-3 border-b border-line-faint text-left">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-line-soft text-text">
            <Glyph name="navigate" size={17} stroke={2.1} />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <b className="text-[15px]">{app.name}</b>
            <span className="text-[12px] text-text-muted">
              {picked === app.key && always ? 'remembered for next time' : ''}
            </span>
          </span>
          {picked === app.key && <span className="text-accent"><Glyph name="check" size={18} stroke={2.6} /></span>}
        </button>
      ))}
      <button type="button" role="checkbox" aria-checked={always} onClick={() => setAlways((on) => !on)}
        className="flex h-[52px] w-full items-center gap-2.5 text-left text-[14px]">
        <span className={cn('inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px]',
          always ? 'bg-accent text-white' : 'border-[1.5px] border-line-strong bg-surface')}>
          {always && <Glyph name="check" size={14} stroke={3} />}
        </span>
        Always use this
      </button>
    </StopSheet>
  )
}
